import * as Y from 'yjs';
import { nanoid } from 'nanoid';
import { withLocalTransaction } from '../transact';
import { yElements } from '../schema';
import { trackCanvasEvent } from '../../analytics';

const AREA_FIELDS = ['name', 'description', 'color', 'border'];
const PADDING = 120;

function makeYArea(area) {
    const yArea = new Y.Map();
    yArea.set('id',     area.id);
    yArea.set('type',   'area');
    yArea.set('x',      area.x ?? 0);
    yArea.set('y',      area.y ?? 0);
    yArea.set('width',  area.width  ?? 0);
    yArea.set('height', area.height ?? 0);
    for (const k of AREA_FIELDS) if (area[k] !== undefined) yArea.set(k, area[k]);
    return yArea;
}

export function addArea(doc, area) {
    let ok = false;
    withLocalTransaction(doc, () => { yElements(doc).set(area.id, makeYArea(area)); ok = true; });
    if (ok) trackCanvasEvent('app_canvas_area_add');
}

export function addAreaForFocusedElements(doc, { focusedElementsIds, areaProps }) {
    let ok = false;
    withLocalTransaction(doc, () => {
        const elements = yElements(doc);
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (const id of focusedElementsIds) {
            const el = elements.get(id);
            if (!el) continue;
            const x = el.get('x'), y = el.get('y'), w = el.get('width'), h = el.get('height');
            if (x < minX) minX = x;
            if (y < minY) minY = y;
            if (x + w > maxX) maxX = x + w;
            if (y + h > maxY) maxY = y + h;
        }

        const yArea = new Y.Map();
        yArea.set('id',     areaProps.id);
        yArea.set('type',   'area');
        yArea.set('x',      minX - PADDING);
        yArea.set('y',      minY - PADDING);
        yArea.set('width',  (maxX - minX) + PADDING * 2);
        yArea.set('height', (maxY - minY) + PADDING * 2);
        for (const k of AREA_FIELDS) if (areaProps[k] !== undefined) yArea.set(k, areaProps[k]);
        yElements(doc).set(areaProps.id, yArea);

        for (const id of focusedElementsIds) {
            const el = elements.get(id);
            if (el) el.set('areaId', areaProps.id);
        }
        ok = true;
    });
    if (ok) trackCanvasEvent('app_canvas_area_add');
}

export function deleteArea(doc, areaId) {
    let ok = false;
    withLocalTransaction(doc, () => {
        const elements = yElements(doc);
        if (!elements.has(areaId)) return;
        elements.forEach((el) => {
            if (el.get('type') === 'area') return;
            if (el.get('areaId') === areaId) el.set('areaId', null);
        });
        elements.delete(areaId);
        ok = true;
    });
    if (ok) trackCanvasEvent('app_canvas_area_delete');
}

export function moveAreas(doc, moves) {
    if (!moves?.length) return;
    withLocalTransaction(doc, () => {
        const areas = yElements(doc);
        for (const { id, x, y, width, height } of moves) {
            const yA = areas.get(id);
            if (!yA) continue;
            if (x      !== undefined) yA.set('x',      x);
            if (y      !== undefined) yA.set('y',      y);
            if (width  !== undefined) yA.set('width',  width);
            if (height !== undefined) yA.set('height', height);
        }
    });
}

// Commits area geometry and element positions in one atomic transaction so
// remote peers never see an intermediate state where areas moved but elements
// haven't yet (or vice versa).
export function moveAreasAndElements(doc, { areaMoves, elementUpdates, areaIdUpdates }) {
    const hasAreas      = areaMoves?.length;
    const hasElements   = elementUpdates?.length;
    const hasAreaIds    = areaIdUpdates?.length;
    if (!hasAreas && !hasElements && !hasAreaIds) return;
    withLocalTransaction(doc, () => {
        if (hasAreas) {
            const areas = yElements(doc);
            for (const { id, x, y, width, height } of areaMoves) {
                const yA = areas.get(id);
                if (!yA) continue;
                if (x      !== undefined) yA.set('x',      x);
                if (y      !== undefined) yA.set('y',      y);
                if (width  !== undefined) yA.set('width',  width);
                if (height !== undefined) yA.set('height', height);
            }
        }
        if (hasElements || hasAreaIds) {
            const elements = yElements(doc);
            if (hasElements) {
                for (const { id, x, y, width, height } of elementUpdates) {
                    const yEl = elements.get(id);
                    if (!yEl) continue;
                    if (x      !== undefined) yEl.set('x',      x);
                    if (y      !== undefined) yEl.set('y',      y);
                    if (width  !== undefined) yEl.set('width',  width);
                    if (height !== undefined) yEl.set('height', height);
                }
            }
            if (hasAreaIds) {
                for (const { id, areaId } of areaIdUpdates) {
                    const yEl = elements.get(id);
                    if (yEl) yEl.set('areaId', areaId);
                }
            }
        }
    });
}

export function setAreaColor(doc, areaId, color) {
    let ok = false;
    withLocalTransaction(doc, () => {
        const a = yElements(doc).get(areaId); if (a) { a.set('color', color); ok = true; }
    });
    if (ok) trackCanvasEvent('app_canvas_area_color_change');
}

export function setAreaBorder(doc, areaId, border) {
    let ok = false;
    withLocalTransaction(doc, () => {
        const a = yElements(doc).get(areaId); if (a) { a.set('border', border); ok = true; }
    });
    if (ok) trackCanvasEvent('app_canvas_area_border_change');
}

export function setAreaDescription(doc, areaId, description) {
    let ok = false;
    withLocalTransaction(doc, () => {
        const a = yElements(doc).get(areaId); if (a) { a.set('description', description); ok = true; }
    });
    if (ok) trackCanvasEvent('app_canvas_area_description_add');
}

export function duplicateArea(doc, areaId, { offsetX = 50, offsetY = 50 } = {}) {
    let newId = null;
    let ok = false;
    withLocalTransaction(doc, () => {
        const areas = yElements(doc);
        const src = areas.get(areaId);
        if (!src) return;
        newId = nanoid();
        const dup = new Y.Map();
        src.forEach((value, key) => {
            if (key === 'id') dup.set('id', newId);
            else if (key === 'x') dup.set('x', value + offsetX);
            else if (key === 'y') dup.set('y', value + offsetY);
            else dup.set(key, value);
        });
        areas.set(newId, dup);
        ok = true;
    });
    if (ok) trackCanvasEvent('app_canvas_area_duplicate');
    return newId;
}
